import { Control, Controller, FieldPath, FieldValues } from "react-hook-form";
import { ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../ui/dropdown-menu";
import { Input } from "../ui/input";
import { useLayoutEffect, useRef, useState } from "react";
import { cn } from "../../lib/utils";

interface ControlledDropdownInputProps<T extends FieldValues> {
  name: FieldPath<T>;
  control: Control<T>;
  options: Record<string, string>;
  label?: string;
  error?: string;
  disabled?: boolean;
}

const ControlledDropdown = <T extends FieldValues>({
  name,
  control,
  options,
  label,
  error,
  disabled = false,
}: ControlledDropdownInputProps<T>) => {
  const inputWrapperRef = useRef<HTMLDivElement>(null);
  const [inputWidth, setInputWidth] = useState<number | null>(null);

  useLayoutEffect(() => {
    if (!inputWrapperRef.current) return;

    const updateWidth = () => {
      const newWidth = inputWrapperRef.current?.offsetWidth;
      if (newWidth && newWidth !== inputWidth) {
        setInputWidth(newWidth);
      }
    };

    updateWidth();

    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(inputWrapperRef.current);

    return () => resizeObserver.disconnect();
  }, [inputWidth]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <div className="flex flex-col w-full gap-2">
        {label && (
          <p>
            {label}
          </p>
        )}
        <div className="relative w-full" ref={inputWrapperRef}>
          <Input
            type="text"
            placeholder=""
            readOnly
            className={cn("w-full", disabled && "cursor-not-allowed")}
            iconOrText={<ChevronDown />}
            iconOrTextPosition="right"
            value={options[field.value as keyof typeof options] || ""}
            disabled={disabled}
          />
          {!disabled && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <div className="absolute inset-0 cursor-pointer" />
              </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className={cn("z-50")}
              style={inputWidth ? { width: inputWidth } : undefined}
            >
              <div className="overflow-auto">
                {Object.entries(options).map(([optionKey, optionLabel]) => (
                  <DropdownMenuItem
                    key={optionKey}
                    onClick={() => field.onChange(optionKey)}
                    className="hover:font-bold"
                  >
                    <div className="px-2 py-1 bg-gray-100 cursor-pointer rounded-md transition-all duration-200">
                      {optionLabel}
                    </div>
                  </DropdownMenuItem>
                ))}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
          )}
        </div>
        {error && <span className="text-red-500 text-sm">{error}</span>}
        </div>
      )}
    />
  );
};

export { ControlledDropdown };
